import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import api, { buyLink } from "@/lib/api";

const CATEGORIES = [
  { key: "all", label: "All Gear" },
  { key: "lures", label: "Lures" },
  { key: "weights", label: "Weights & Sinkers" },
  { key: "line", label: "Line" },
  { key: "apparel", label: "Apparel" },
  { key: "accessories", label: "Accessories" },
];

export default function Gear() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (cat === "all") return products;
    return products.filter((p) => p.category === cat);
  }, [products, cat]);

  return (
    <div>
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="label-eyebrow mb-4">Eco-approved catalog</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight mb-6 max-w-4xl">
            Tackle that <span className="italic text-[#C95B3B]">respects</span> the water.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Every product here is lead-free, biodegradable, or made from recycled materials — hand-picked
            to keep you legal at the parks going non-lead in 2026.
          </p>
        </div>
      </section>

      <section className="sticky top-16 z-30 glass-header">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex gap-2 overflow-x-auto" data-testid="gear-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`gear-filter-${c.key}`}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-colors duration-200 border ${
                cat === c.key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent border-border hover:border-foreground"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
          <p className="text-xs text-muted-foreground mb-6 border-l-2 border-[#C95B3B] pl-3" data-testid="affiliate-disclosure">
            <span className="font-semibold text-foreground">Affiliate disclosure:</span> Some links below are affiliate links.
            As an Amazon Associate, ecofisherman.com earns from qualifying purchases. You pay nothing extra — commissions come out of the retailer's margin and help keep this site running.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p, i) => (
              <motion.a
                key={p.id}
                href={buyLink(p.id, "gear")}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 8) * 0.04 }}
                className="card-flat block group"
                data-testid={`product-card-${p.id}`}
              >
                <div className="aspect-square bg-[#E6E2D6] overflow-hidden relative">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute top-3 left-3 flex flex-col gap-1">
                    {p.badges.map((b) => <span key={b} className="eco-badge bg-white">{b}</span>)}
                  </div>
                </div>
                <div className="p-5">
                  <div className="label-eyebrow mb-1">{p.brand}</div>
                  <div className="font-display text-xl leading-tight mb-2">{p.name}</div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{p.description}</p>
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="font-mono text-base font-semibold">{p.price}</span>
                    <span className="text-sm text-[#C95B3B] font-semibold inline-flex items-center gap-1">
                      Buy <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">No products in this category yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}
